import { Link, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Input, Button } from '@/components/common'
import { useAuth } from '@/hooks/useAuth'
import { useToast } from '@/contexts/ToastContext'
import { useSEO } from '@/hooks/useSEO'

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
})

type LoginFormData = z.infer<typeof loginSchema>

export const Login = () => {
  const location = useLocation()
  const { login } = useAuth()
  const { showToast } = useToast()
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useSEO({
    title: 'Login - Binder Tech Solutions',
    description: 'Sign in to your Binder Tech Solutions account to access BLIS and IZG platforms. Manage your subscriptions, track usage, and monitor your assets.',
    keywords: 'login, sign in, Binder Tech Solutions, account access',
    url: 'https://bindertech.com/login',
    type: 'website',
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    setError('')
    setIsLoading(true)

    try {
      await login(data.email, data.password)

      showToast('Welcome back!', 'success')

      // Use window.location.href for a hard redirect that bypasses React Router state checks
      // This ensures navigation happens even if App component hasn't re-rendered yet
      const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard'
      window.location.href = from
    } catch (err: any) {
      // Extract error message from API response
      let errorMessage = 'Login failed. Please try again.'
      
      if (err?.response?.data?.detail) {
        // Use the specific error message from the backend
        errorMessage = err.response.data.detail
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message
      } else if (err?.message) {
        errorMessage = err.message
      }
      
      setError(errorMessage)
      showToast(errorMessage, 'error')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark px-4 py-10">
      <div className="w-full max-w-[520px]">
        <div className="mb-8 flex flex-col items-center gap-2">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-white">
              <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>
                token
              </span>
            </div>
            <span className="text-xl font-bold tracking-tight text-text-main-light dark:text-text-main-dark">
              Binder Tech
            </span>
          </Link>
        </div>

        <div className="rounded-xl bg-surface-light dark:bg-surface-dark shadow-sm border border-border-light dark:border-border-dark p-6 sm:p-8">
          <div className="flex flex-col gap-2 mb-6">
            <h1 className="text-2xl font-bold leading-tight text-text-main-light dark:text-text-main-dark">
              Welcome back
            </h1>
            <p className="text-text-sub-light dark:text-text-sub-dark text-sm font-normal">
              Sign in to access BLIS and IZG intelligence tools.
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
            <Input
              label="Email"
              type="email"
              {...register('email')}
              placeholder="name@company.com"
              error={errors.email?.message}
              required
            />

            <Input
              label="Password"
              type="password"
              {...register('password')}
              placeholder="Enter your password"
              error={errors.password?.message}
              required
            />

            <div className="flex items-center justify-between">
              <a
                href="mailto:contact-us@binder.com
                ?subject=Forgot%20Password%20Request
                &body=Hello,%0D%0A%0D%0AI%20would%20like%20to%20request%20a%20password%20reset%20request.%0D%0A%0D%0AORGANIZATION%20NAME:%0D%0AEMAIL:%0D%0A%0D%0AThank%20you."
                className="text-sm text-primary hover:underline"
              >
                Forgot password?
              </a>
            </div>

            <Button type="submit" variant="primary" isLoading={isLoading} className="w-full">
              Sign In
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}

